import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import {
  clearFeatureFlagOverrides,
  getBuildFeatureFlags,
  getFeatureFlagOverrides,
  getFeatureFlags,
  setFeatureFlagOverride,
  type FeatureFlagKey,
  type FeatureFlags,
} from "../lib/featureFlags"

type FeatureFlagsContextValue = {
  flags: FeatureFlags
  buildFlags: FeatureFlags
  overrides: Partial<Record<FeatureFlagKey, boolean>>
  setFlag: (key: FeatureFlagKey, enabled: boolean) => void
  resetFlags: () => void
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null)

export const FeatureFlagsProvider = ({ children }: { children: React.ReactNode }) => {
  const [flags, setFlags] = useState<FeatureFlags>(() => getFeatureFlags())
  const [overrides, setOverrides] = useState(() => getFeatureFlagOverrides())
  const buildFlags = useMemo(() => getBuildFeatureFlags(), [])

  const refresh = useCallback(() => {
    setFlags(getFeatureFlags())
    setOverrides(getFeatureFlagOverrides())
  }, [])

  useEffect(() => {
    const handleChange = () => refresh()

    window.addEventListener("dc-feature-flags-changed", handleChange)
    window.addEventListener("storage", handleChange)

    return () => {
      window.removeEventListener("dc-feature-flags-changed", handleChange)
      window.removeEventListener("storage", handleChange)
    }
  }, [refresh])

  const setFlag = useCallback(
    (key: FeatureFlagKey, enabled: boolean) => {
      setFeatureFlagOverride(key, enabled)
      refresh()
    },
    [refresh]
  )

  const resetFlags = useCallback(() => {
    clearFeatureFlagOverrides()
    refresh()
  }, [refresh])

  const value = useMemo(
    () => ({
      flags,
      buildFlags,
      overrides,
      setFlag,
      resetFlags,
    }),
    [flags, buildFlags, overrides, setFlag, resetFlags]
  )

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>
}

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext)
  if (!context) {
    throw new Error("useFeatureFlags must be used within FeatureFlagsProvider")
  }
  return context
}
